import { TextField, Textarea, ProjectSelector, UserSelector } from "../../../../shared";
import { Stack, Typography, Box, FormLabel } from "@mui/joy";
import { Button } from "../../../../shared";
import { BadgePlus } from "lucide-react";
import { FormProvider } from "react-hook-form";
import { VALIDATION_RULES } from "./constants";
import { TaskStatusSelect, ColorPicker } from "./utils";
import { useNewSubTask } from "./hooks";

interface NewSubTaskInlineProps {
    selectedDate: any;
    onSubmit: (data: any) => void;
    onFormStateChange?: (isValid: boolean) => void;
}

export default function NewSubTaskInline(props: NewSubTaskInlineProps) {
    const { selectedDate, onSubmit, onFormStateChange } = props;

    // Use custom hook for business logic
    const { methods, handleSubmit, isLoading } = useNewSubTask({
        selectedDate,
        onFormStateChange,
    });

    return (
        <FormProvider {...methods}>
            <form id="new-task-form" onSubmit={methods.handleSubmit((data) => handleSubmit(data, onSubmit))}>
                <Stack spacing={3} sx={{ mt: 1 }}>
                    <TextField
                        name="eventName"
                        label="Task summary"
                        placeholder="Enter task summary"
                        helperText="Brief description of the task or event"
                        rules={VALIDATION_RULES.eventName}
                    />

                    <Textarea
                        name="description"
                        label="Description"
                        placeholder="Add description..."
                        helperText="Optional: Additional details about the task"
                    />
                    <ColorPicker watch={methods.watch} setValue={methods.setValue} />
                    <Box display="flex" gap={2}>
                        <ProjectSelector
                            placeholder="Select project"
                            label="Project"
                            name="projectId"
                            helperText="Choose the project this task belongs to"
                            rules={VALIDATION_RULES.projectId}
                        />
                    </Box>

                    <Box display="flex" gap={2}>
                        <Box flex={1}>
                            <FormLabel>Task Status *</FormLabel>
                            <Typography level="body-xs" sx={{ color: "text.secondary", mb: 1 }}>
                                Current status of the task
                            </Typography>
                            <TaskStatusSelect
                                control={methods.control}
                                rules={VALIDATION_RULES.taskStatus}
                                name="taskStatus"
                            />
                        </Box>
                        <UserSelector
                            placeholder="Select assignee"
                            label="Assignee"
                            name="assignee"
                            helperText="Choose who will work on this task"
                            rules={VALIDATION_RULES.assignee}
                        />
                    </Box>

                    <Box display="flex" gap={2}>
                        <Box flex={1}>
                            <TextField
                                name="startDateTime"
                                label="Start Date & Time"
                                type="datetime-local"
                                helperText="When the task begins"
                                rules={VALIDATION_RULES.startDateTime}
                            />
                        </Box>
                        <Box flex={1}>
                            <TextField
                                name="endDateTime"
                                label="End Date & Time"
                                type="datetime-local"
                                helperText="When the task ends"
                                rules={VALIDATION_RULES.endDateTime}
                            />
                        </Box>
                    </Box>

                    <Box display="flex" justifyContent="flex-start" gap={2} mt={1}>
                        <Button
                            type="submit"
                            loading={isLoading}
                            disabled={!methods.formState.isValid}
                            startDecorator={<BadgePlus size={16} />}
                        >
                            Create new task
                        </Button>
                        <Button variant={"outlined"}>Cancel</Button>
                    </Box>
                </Stack>
            </form>
        </FormProvider>
    );
}
